import { FC, useEffect, useState } from 'react'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { oportunidadeService } from '@/services/oportunidadeService'
import { Oportunidade } from '@/types/oportunidade'
import {
  MOTIVO_AGENDAMENTO_INDISPONIVEL,
  STATUS_LABEL,
  STATUS_TERMINAIS,
  permiteAgendamento,
} from '@/types/statusReserva'
import { formatCNPJ } from '@/utils/formatters'
import { termoPareceCnpj } from '@/utils/cnpj'

/**
 * Seletor de reserva da Nova Reunião (pedido de teste do usuário, 10/10/2026): digita-se o CNPJ
 * ou o nome e a reunião passa a ser VINCULADA à reserva — puxa empresa e CNPJ e conta para o
 * funil (desde 16/10/2026 a reunião é opcional — a Originei a solicita quando precisa). A busca é no servidor e no
 * escopo de quem pede; reservas que ainda não podem agendar aparecem desabilitadas, com o
 * motivo (regra de 06/09: reunião vinculada só depois da aprovação da Originei — o servidor
 * recusa de qualquer forma).
 */

/**
 * Termo que vale uma consulta: 3+ caracteres de CNPJ (numérico ou alfanumérico, máscara
 * ignorada) ou 2+ letras (nome). Espelha `OportunidadeService.buscar` do backend.
 */
export const termoBuscavel = (termo: string): boolean => {
  const t = termo.trim()
  const temLetra = /\p{L}/u.test(t)
  return termoPareceCnpj(t) || (temLetra && t.length >= 2)
}

export const nomeDaReserva = (r: Pick<Oportunidade, 'razaoSocial' | 'nomeFantasia' | 'nomeCliente'>): string =>
  r.razaoSocial || r.nomeFantasia || r.nomeCliente || 'Empresa sem nome'

export const motivoIndisponivel = (r: Pick<Oportunidade, 'status'>): string | null => {
  if (permiteAgendamento(r.status)) return null
  if (r.status && STATUS_TERMINAIS.includes(r.status)) return 'Reserva encerrada.'
  return MOTIVO_AGENDAMENTO_INDISPONIVEL
}

const rotulo = (r: Oportunidade): string =>
  `${r.numero != null ? `RES-${String(r.numero).padStart(4, '0')} · ` : ''}${nomeDaReserva(r)}`

interface SeletorReservaReuniaoProps {
  value: Oportunidade | null
  onChange: (reserva: Oportunidade | null) => void
  autoFocus?: boolean
}

export const SeletorReservaReuniao: FC<SeletorReservaReuniaoProps> = ({ value, onChange, autoFocus }) => {
  const [termo, setTermo] = useState('')
  const [opcoes, setOpcoes] = useState<Oportunidade[]>([])
  const [carregando, setCarregando] = useState(false)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    if (!termoBuscavel(termo) || (value && termo === rotulo(value))) {
      setOpcoes(value ? [value] : [])
      setCarregando(false)
      return
    }
    let ativo = true
    setCarregando(true)
    setFalhou(false)
    const t = setTimeout(() => {
      oportunidadeService
        .buscarPorTermo(termo.trim())
        .then((page) => { if (ativo) setOpcoes(page.content ?? []) })
        .catch(() => { if (ativo) { setOpcoes([]); setFalhou(true) } })
        .finally(() => { if (ativo) setCarregando(false) })
    }, 300)
    return () => { ativo = false; clearTimeout(t) }
  }, [termo, value])

  const semOpcoes = falhou
    ? 'Não foi possível buscar as reservas. Tente de novo.'
    : termoBuscavel(termo)
      ? 'Nenhuma reserva sua com esse CNPJ ou nome.'
      : 'Digite ao menos 3 caracteres do CNPJ ou 2 letras do nome.'

  return (
    <Autocomplete<Oportunidade, false, false, false>
      options={opcoes}
      value={value}
      loading={carregando}
      filterOptions={(x) => x}
      onChange={(_, r) => onChange(r)}
      onInputChange={(_, v) => setTermo(v)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={rotulo}
      getOptionDisabled={(r) => motivoIndisponivel(r) !== null}
      noOptionsText={semOpcoes}
      loadingText="Buscando reservas…"
      renderOption={(props, r) => {
        const motivo = motivoIndisponivel(r)
        return (
          <li {...props} key={r.id}>
            <Box>
              <Typography variant="body2" fontWeight={600}>{rotulo(r)}</Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {[r.cnpj ? `CNPJ ${formatCNPJ(r.cnpj)}` : null, r.tipoNecessidade, r.status ? STATUS_LABEL[r.status] : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {motivo && (
                <Typography variant="caption" color="warning.main" component="div">{motivo}</Typography>
              )}
            </Box>
          </li>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Reserva"
          placeholder="CNPJ ou razão social"
          autoFocus={autoFocus}
          helperText={
            value
              ? 'A reunião fica vinculada a esta reserva e conta para o avanço dela no funil.'
              : 'Sem reserva vinculada, a reunião fica só na agenda e não conta para o avanço da reserva no funil.'
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {carregando ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
